import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCommentsQuery, useGetNoteQuery} from '@dms/api/core';
import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetWorkshopInterestQuery} from '@dms/api/workshop';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {customerRoutes, testIds, workshopRoutes} from '@dms/routes';
import {
  Checklists,
  DetailTemplate,
  EMPTY_PLACEHOLDER,
  EntityHeaderFlag,
  NavigationItem,
  getCustomerEmail,
  getCustomerName,
  getCustomerPhoneNumber,
  getTimeToFirstResponse,
  isCustomerNaturalPerson,
  Section,
  SnippetProps,
  useChecklist,
  useWorkshopInterestEditPermissions,
} from '@dms/shared';

import {buildArray, composePath, parseDate, useNavigate, useRequiredParams} from 'shared/web';

import {ActionLog} from './(sections)/ActionLog/ActionLog';
import {Customer} from './(sections)/Customer/Customer';
import {Overview} from './(sections)/Overview/Overview';
import {Ownership} from './(sections)/Ownership/Ownership';
import {ServiceRequest} from './(sections)/ServiceRequest/ServiceRequest';
import {WorkshopInterestActivityPanel} from './components/WorkshopInterestActivityPanel';
import {useWorkshopInterestActions} from './hooks/useWorkshopInterestActions';

export function WorkshopInterestDetail() {
  const {id: interestId} = useRequiredParams();
  const formatDateTime = useDateTimeFormatter();
  const navigate = useNavigate();

  const {
    data: workshopInterest,
    isLoading,
    isError,
  } = useGetWorkshopInterestQuery({id: interestId});

  const isCurrentInterest = workshopInterest?.id === interestId;

  const {currentData: customer} = useGetCustomerV2Query(
    isCurrentInterest && isNotNil(workshopInterest?.customerId)
      ? {customerId: workshopInterest.customerId}
      : skipToken,
    {refetchOnMountOrArgChange: true}
  );
  const vehicleId = isCurrentInterest ? workshopInterest?.vehicleId : undefined;

  const {vehicleOperator} = useGetVehicleCustomerQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: (result) => ({
        vehicleOperator: result.currentData?.find(
          (item) => item?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
        ),
      }),
    }
  );

  const {currentData: operatorCustomerData} = useGetCustomerV2Query(
    isNotNil(vehicleOperator?.customerId) ? {customerId: vehicleOperator.customerId} : skipToken,
    {refetchOnMountOrArgChange: true}
  );

  const operatorCustomer = isNotNil(vehicleId) ? operatorCustomerData : undefined;

  const {data: note} = useGetNoteQuery({
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
    context: EntityResourceIds.workshopInterest,
  });

  const {data: comments = []} = useGetCommentsQuery({
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
  });

  const {tag, participation} = useWorkshopInterestEditPermissions({interestId});

  const actions = useWorkshopInterestActions(interestId);

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: interestId,
    resourceId: 'WORKSHOP_INTEREST',
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.workshopInterest,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.workshopInterest,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {timeToFirstResponse, hasEnded} = getTimeToFirstResponse(
    workshopInterest,
    workshopInterest?.firstContactAt
  );

  const title = getCustomerName(customer) ?? EMPTY_PLACEHOLDER;

  const flags = buildArray<EntityHeaderFlag>()
    .when(workshopInterest?.state === 'NEW', {
      colorScheme: 'blue',
      label: i18n.t('general.labels.new'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'OPEN', {
      colorScheme: 'green',
      label: i18n.t('general.actions.open'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'CLOSED', {
      colorScheme: 'green',
      label: i18n.t('general.labels.closed'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'UNSUCCESSFUL', {
      colorScheme: 'red',
      label: i18n.t('general.labels.unsuccessful'),
      isSubtle: true,
    })
    .add({
      colorScheme: 'orange',
      label: i18n.t('page.workshop.labels.interest'),
      isSubtle: false,
    });

  const onNoteCommentIconClick = () => {
    navigate(composePath(workshopRoutes.interestDetailOverview, {params: {id: interestId}}));
  };

  const parameters = buildArray<Parameter>()
    .when(isNotNilOrEmpty(note?.text), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: note?.text,
      onClick: onNoteCommentIconClick,
    })
    .when(comments.length > 0, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: onNoteCommentIconClick,
    })
    .when(isNotNil(workshopInterest?.publicId), workshopInterest?.publicId)
    .when(
      isNotNil(workshopInterest?.createdAt),
      `${i18n.t('general.labels.created')}: ${
        isNotNil(workshopInterest?.createdAt)
          ? formatDateTime('dateTimeShort', parseDate(workshopInterest.createdAt))
          : EMPTY_PLACEHOLDER
      }`
    )
    .when(isNotNilOrEmpty(timeToFirstResponse), {
      title: timeToFirstResponse,
      icon: 'devices/access_time',
      variant: hasEnded ? 'success' : 'danger',
      tooltip: i18n.t('general.label.timeToFirstResponse'),
    });

  const snippets = buildArray<SnippetProps>()
    .add({
      'data-testid': testIds.workshop.interestDetail('customer-snippet'),
      resourceId: EntityResourceIds.workshopInterest,
      recordId: interestId,
      label: i18n.t('entity.customer.labels.customer'),
      placeholder: {
        title: i18n.t('entity.customer.labels.customer'),
      },
      title: getCustomerName(customer) ?? EMPTY_PLACEHOLDER,
      parameters: buildArray<string | null>()
        .when(
          isNotNil(customer) && !isCustomerNaturalPerson(customer),
          getCustomerPhoneNumber(customer)
        )
        .when(isNotNil(customer) && !isCustomerNaturalPerson(customer), getCustomerEmail(customer))
        .when(
          isNotNil(customer) && isCustomerNaturalPerson(customer),
          getCustomerPhoneNumber(customer)
        )
        .when(isNotNil(customer) && isCustomerNaturalPerson(customer), getCustomerEmail(customer)),
      href: customer?.id
        ? composePath(customerRoutes.detail, {
            params: {id: customer.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
      customerId: customer?.id,
      selectedContactId: customer?.foundingPerson?.id,
    })
    .add({
      'data-testid': testIds.workshop.interestDetail('operator-snippet'),
      label: i18n.t('entity.ownership.label.vehicleOperator'),
      placeholder: {
        title: i18n.t('entity.ownership.actions.assignVehicleOperator'),
      },
      title: getCustomerName(operatorCustomer) ?? EMPTY_PLACEHOLDER,
      parameters: buildArray<string | null>()
        .when(
          isNotNil(operatorCustomer) && !isCustomerNaturalPerson(operatorCustomer),
          getCustomerPhoneNumber(operatorCustomer)
        )
        .when(
          isNotNil(operatorCustomer) && !isCustomerNaturalPerson(operatorCustomer),
          getCustomerEmail(operatorCustomer)
        )
        .when(
          isNotNil(operatorCustomer) && isCustomerNaturalPerson(operatorCustomer),
          getCustomerPhoneNumber(operatorCustomer)
        )
        .when(
          isNotNil(operatorCustomer) && isCustomerNaturalPerson(operatorCustomer),
          getCustomerEmail(operatorCustomer)
        ),
      href: operatorCustomer?.id
        ? composePath(customerRoutes.detail, {
            params: {id: operatorCustomer.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
    });

  const navigation = buildArray<NavigationItem>()
    .add({
      id: 'overview',
      label: i18n.t('general.labels.overview'),
      href: composePath(workshopRoutes.interestDetailOverview, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-overview'),
      content: <Overview interestId={interestId} />,
    })
    .add({
      id: 'customer',
      label: i18n.t('entity.customer.labels.customer'),
      href: composePath(workshopRoutes.interestDetailCustomer, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-customer'),
      content: <Customer interestId={interestId} />,
    })
    .add({
      id: 'ownership',
      label: i18n.t('entity.customer.labels.ownership'),
      href: composePath(workshopRoutes.interestDetailOwnership, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-ownership'),
      content: <Ownership interestId={interestId} />,
    })
    .add({
      id: 'serviceRequest',
      label: i18n.t('entity.order.labels.serviceRequest'),
      href: composePath(workshopRoutes.interestDetailServiceRequest, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-serviceRequest'),
      content: <ServiceRequest interestId={interestId} />,
      hasSeparator: true,
    })
    .add({
      id: 'checklist',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(workshopRoutes.interestDetailChecklist, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-checklist'),
      content: (
        <Section>
          <Checklists {...checklistSettings} />
        </Section>
      ),
      badge: badgeSettings,
    })
    .add({
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(workshopRoutes.interestDetailTasks, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-tasks'),
      content: (
        <TaskGridPage
          gridCode="workshop-interest-task"
          entityId={interestId}
          data-testid={testIds.workshop.interestDetail('tasks')}
        />
      ),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      hasSeparator: true,
    })
    .add({
      id: 'actionLog',
      label: i18n.t('general.labels.activityLogs'),
      href: composePath(workshopRoutes.interestDetailActionLog, {params: {id: interestId}}),
      'data-testid': testIds.workshop.interestDetail('section-actionLog'),
      content: <ActionLog />,
    });

  return (
    <>
      <Helmet title={i18n.t('page.workshop.labels.workshopInterest')} />
      <DetailTemplate
        key={workshopInterest?.id}
        isLoading={isLoading}
        isError={isError}
        activityPanel={<WorkshopInterestActivityPanel interestId={interestId} />}
        header={{
          title,
          icon: 'content/next_week',
          flags,
          parameters,
          snippets,
          recordId: interestId,
          resourceId: EntityResourceIds.workshopInterest,
          controls: ['WORKSHOP_INTEREST_BRANCH', 'WORKSHOP_INTEREST_SOURCE', 'ASSIGNEE'],
          actions,
        }}
        navigation={navigation}
        data-testid={testIds.workshop.interestDetail('page')}
        permissions={{
          tag,
          participation,
        }}
      />
    </>
  );
}
